import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { logout } from '~/lib/logout';

const authority = import.meta.env.VITE_OIDC_AUTHORITY || 'https://auth.tbit.app';

let userManager: UserManager | null = null;

function getUserManager() {
  if (!userManager) {
    userManager = new UserManager({
      authority,
      // Same OIDC client web-exapps uses. This client runs on its OWN origin, so
      // that origin's /auth/callback has to be added to the client's registered
      // redirect_uris in auth.tbit.app or the login redirect comes back rejected.
      client_id: 'platform-tbit-app',
      redirect_uri: `${window.location.origin}/auth/callback`,
      response_type: 'code',
      scope: 'openid email profile',
      automaticSilentRenew: false,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      post_logout_redirect_uri: window.location.origin,
    });
  }
  return userManager;
}

export const SIGNUP_INTENT_KEY = 'job_apply_signup_intent';
export const SIGNED_IN_KEY = 'job_apply_signed_in';

/**
 * «Crear mi perfil gratis» de tbit.app/es/job-apply llega con `?signup=1`. Se
 * guarda antes de montar React porque la ruta protegida redirige a /login y
 * ahí ya no está la URL con la que se entró.
 */
export function captureSignupIntent(search = window.location.search) {
  try {
    if (new URLSearchParams(search).get('signup') === '1') sessionStorage.setItem(SIGNUP_INTENT_KEY, '1');
  } catch {
    // Sin almacenamiento, el login de siempre.
  }
}

/**
 * Lo que viaja en el authorize además de lo estándar.
 *
 * `magicToken`: el enlace mágico del correo (`/auth/continue?token=`, ver
 * `ContinuePage`). Viaja como `magic_token` y auth.tbit.app lo canjea en el
 * prompt de login, una sola vez.
 *
 * `screen_hint=signup`: quien llega a registrarse y nunca entró desde este
 * navegador ve «Crear cuenta» y no «Bienvenido de nuevo» (2026-10-14: los que
 * venían del anuncio de TikTok caían en la pantalla de entrar y no seguían).
 * Quien ya entró alguna vez aquí ve la de siempre.
 */
export function loginParams(magicToken?: string): Record<string, string> | undefined {
  if (magicToken) return { magic_token: magicToken };
  try {
    if (sessionStorage.getItem(SIGNUP_INTENT_KEY) && !localStorage.getItem(SIGNED_IN_KEY)) {
      return { screen_hint: 'signup' };
    }
  } catch {
    // Sin almacenamiento, el login de siempre.
  }
  return undefined;
}

export function startLogin(magicToken?: string) {
  const extraQueryParams = loginParams(magicToken);
  return getUserManager().signinRedirect(extraQueryParams ? { extraQueryParams } : undefined);
}

export async function handleCallback(): Promise<string> {
  const user = await getUserManager().signinRedirectCallback();
  if (!user?.id_token) {
    throw new Error('No id_token received from OIDC provider');
  }
  try {
    localStorage.setItem(SIGNED_IN_KEY, '1');
    sessionStorage.removeItem(SIGNUP_INTENT_KEY);
  } catch {
    // Sin almacenamiento no hay marca; la próxima vez sale la pantalla de siempre.
  }
  return user.id_token;
}

/**
 * Salir. Delega en `lib/logout.ts` en vez de usar `signoutRedirect()` porque
 * oidc-client-ts arma el end_session con el `post_logout_redirect_uri` de arriba
 * —el ORIGEN de quien esté corriendo— y el proveedor sólo acepta los destinos
 * registrados. Además hay que borrar cookies y borradores que oidc-client-ts no
 * conoce: una sola puerta de salida, y está allá.
 */
export function startLogout() {
  logout();
}
